import { useCallback, useRef } from 'react';

/**
 * Snapshot-based cancel/restore for the Select Design Area crop modal.
 *
 * @param {{
 *   cropMode: string;
 *   crop: { x: number; y: number };
 *   zoom: number;
 *   hasValidCrop: boolean;
 *   croppedPreviewUrl: string;
 *   cropPixelsRef: import('react').MutableRefObject<Record<string, unknown> | null>;
 *   setCropMode: (mode: string) => void;
 *   setCrop: (crop: { x: number; y: number }) => void;
 *   setZoom: (zoom: number) => void;
 *   setHasValidCrop: (valid: boolean) => void;
 *   setCroppedPreviewUrl: (updater: string | ((prev: string) => string)) => void;
 *   setCropPopupOpen: (open: boolean) => void;
 * }} params
 */
export function useDesignAreaCropSnapshot({
  cropMode,
  crop,
  zoom,
  hasValidCrop,
  croppedPreviewUrl,
  cropPixelsRef,
  setCropMode,
  setCrop,
  setZoom,
  setHasValidCrop,
  setCroppedPreviewUrl,
  setCropPopupOpen,
}) {
  const snapshotRef = useRef(
    /** @type {null | {
     *   cropMode: string;
     *   crop: { x: number; y: number };
     *   zoom: number;
     *   cropPixels: Record<string, unknown> | null;
     *   hasValidCrop: boolean;
     *   croppedPreviewUrl: string;
     * }} */
    (null),
  );

  const setCropModalSnapshot = useCallback((snap) => {
    snapshotRef.current = {
      cropMode: snap.cropMode,
      crop: { ...snap.crop },
      zoom: snap.zoom,
      cropPixels: snap.cropPixels ? { ...snap.cropPixels } : null,
      hasValidCrop: snap.hasValidCrop,
      croppedPreviewUrl: snap.croppedPreviewUrl,
    };
  }, []);

  const saveCropModalSnapshot = useCallback(() => {
    const pixels = cropPixelsRef.current;
    snapshotRef.current = {
      cropMode,
      crop: { ...crop },
      zoom,
      cropPixels: pixels ? { ...pixels } : null,
      hasValidCrop,
      croppedPreviewUrl,
    };
  }, [
    crop,
    cropMode,
    cropPixelsRef,
    croppedPreviewUrl,
    hasValidCrop,
    zoom,
  ]);

  const openCropModal = useCallback(() => {
    saveCropModalSnapshot();
    setCropPopupOpen(true);
  }, [saveCropModalSnapshot, setCropPopupOpen]);

  const clearSnapshot = useCallback(() => {
    snapshotRef.current = null;
  }, []);

  const cancelCropModal = useCallback(() => {
    const snap = snapshotRef.current;
    if (snap) {
      setCropMode(snap.cropMode);
      setCrop({ ...snap.crop });
      setZoom(snap.zoom);
      cropPixelsRef.current = snap.cropPixels ? { ...snap.cropPixels } : null;
      setHasValidCrop(snap.hasValidCrop);
      setCroppedPreviewUrl((current) => {
        if (current && current !== snap.croppedPreviewUrl) {
          URL.revokeObjectURL(current);
        }
        return snap.croppedPreviewUrl;
      });
    }
    snapshotRef.current = null;
    setCropPopupOpen(false);
  }, [
    cropPixelsRef,
    setCrop,
    setCropMode,
    setCroppedPreviewUrl,
    setCropPopupOpen,
    setHasValidCrop,
    setZoom,
  ]);

  return {
    openCropModal,
    saveCropModalSnapshot,
    setCropModalSnapshot,
    cancelCropModal,
    clearSnapshot,
    snapshotRef,
  };
}
